<script lang="ts">
	//Svelte
	import { createEventDispatcher, onDestroy, onMount, tick } from 'svelte';
	import { location } from 'svelte-spa-router';
	//Services
	import { format } from 'svelte-i18n';
	import { mobile } from 'src/services/mobile-rework';
	// Stores
	import { profile_data } from 'src/stores/account/user';
	import { devices } from 'src/stores/lists/devices';
	import { isNavScrollBottomExpandEnabled } from 'src/stores/features/dev/nav-scroll-bottom-expand';
	import { isNavScrollCompactEnabled } from 'src/stores/features/dev/nav-scroll-compact';
	import {
		navDockWideEnabled,
		navIndicatorCircleEnabled,
	} from 'src/stores/features/dev/nav-dock-style';
	import { isNavDockHorizontalSelectEnabled } from 'src/stores/features/dev/nav-dock-horizontal-select';
	import { edit } from 'src/stores/ui/edit_lists';
	// Ensure `--floating-dock-fade-height` is applied whenever the dock is mounted.
	import 'src/stores/features/dev/nav-dock-fade';
	// Helpers
	import { hasCameraDevices } from 'src/components/Camera/helpers/camera-helpers';
	import { isEmailUnverified } from 'src/helpers/email_verification_helpers';
	import {
		appendMissingMenuOrderIndices,
		menuItemCameraMonitoring,
		menuItemsNewLayout,
		menuItemsNewLayoutMore,
		MORE_MENU_ICON,
		parseHomeDataMenuOrderJson,
		partitionNewLayoutMenuOrderIndices,
		type MenuItemDef as MenuItemType,
	} from 'src/helpers/main_menu';
	import {
		createScrollChromeTracker,
		isScrollChromeTarget,
		isScrollNearBottom,
		resolveScrollChromeCollapsed,
	} from 'src/helpers/scroll_chrome_helpers';
	import ChatWindow from 'src/components/AISceneChatbot/ChatWindow.svelte';
	import {
		PillShapedDock,
		DropdownMenu,
		MenuItemList,
	} from '@Shelly/shelly-design-system';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';

	const dispatch = createEventDispatcher<{
		'menu-item-click': { menuItem: MenuItemType };
		'logo-click': void;
	}>();

	export let activeMenuItem: MenuItemType;
	export let menuItems = [...menuItemsNewLayout, ...menuItemsNewLayoutMore];
	export let visualActiveMenuId: string | null = null;

	let windowInnerWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
	let navShellRef: HTMLElement;
	let navBarRef: HTMLElement;
	let navIndicatorRef: HTMLElement;
	let navItemsRefs: HTMLElement[] = [];
	let indicatorX = 0;
	let activeRenderIndex = -1;
	let indicatorVisible = false;
	let loadedMenuId: string | null = null;
	let isNavCompact = false;
	let activeModalKey: string | null = null;
	let isModalClosing = false;
	let closeNavModalTimer: ReturnType<typeof setTimeout> | null = null;
	let navModalBackButtonCleanup: (() => void) | null = null;
	const NAV_EDGE_INSET = 5;
	const NAV_EXPAND_ANIMATION_MS = 400;
	const LOGO_MENU_ITEM_ID = 'shelly-logo';
	const MORE_MENU_ITEM_ID = 'more-menu-item';
	const ASSISTANT_MENU_ITEM_ID = 'assistant';
	const DESKTOP_NAV_BREAKPOINT = SystemScreenBreakpoints.DESKTOP_MIN;
	const logoMenuItem: MenuItemType = {
		id: LOGO_MENU_ITEM_ID,
		href: '',
		icon: '',
		label: 'button_go_to_shelly',
		labelDefault: 'Go to Shelly',
	};
	const moreMenuItem: MenuItemType = {
		id: MORE_MENU_ITEM_ID,
		href: '',
		icon: MORE_MENU_ICON,
		label: 'title_more',
		labelDefault: 'More',
	};
	let showMoreDropdown = false;
	const navScrollChrome = createScrollChromeTracker();

	const clearCloseNavModalTimer = () => {
		if (!closeNavModalTimer) return;
		clearTimeout(closeNavModalTimer);
		closeNavModalTimer = null;
	};

	const getSolidIcon = (icon: string) => {
		const [weight, name] = icon.split(' ');
		if (!name) return icon;
		if (weight === 'fak') return icon;
		return `fas ${name}`;
	};

	const handlerClickMenuItem = (menuItem: MenuItemType) => {
		clearCloseNavModalTimer();
		isModalClosing = false;
		activeModalKey = null;
		activeMenuItem = menuItem;
		dispatch('menu-item-click', { menuItem });
	};

	const handleLogoClick = () => {
		showMoreDropdown = false;
		dispatch('logo-click');
	};

	const isMoreSubItemActive = (subItem: MenuItemType, loc: string) => {
		const matches = moreMenuItems.filter((item) => item.href && loc.startsWith(item.href));
		if (!matches.length) return false;
		const best = matches.reduce((a, b) => ((b.href?.length ?? 0) > (a.href?.length ?? 0) ? b : a));
		return best.id === subItem.id;
	};

	const handleClickMoreBtn = () => {
		showMoreDropdown = !showMoreDropdown;
	};

	const handleCloseMoreBtn = () => {
		showMoreDropdown = false;
	};

	const handleClickMoreSubItem = (subMenuItem: MenuItemType) => {
		handleCloseMoreBtn();
		handlerClickMenuItem(subMenuItem);
	};

	const isMobileViewport = () => {
		const width =
			windowInnerWidth ||
			(typeof window !== 'undefined' ? window.innerWidth : DESKTOP_NAV_BREAKPOINT);
		return width < DESKTOP_NAV_BREAKPOINT;
	};

	const closeNavModal = () => {
		if (!activeModalKey || isModalClosing) return;
		isNavCompact = false;
		isModalClosing = true;
		clearCloseNavModalTimer();
		closeNavModalTimer = setTimeout(() => {
			activeModalKey = null;
			isModalClosing = false;
			closeNavModalTimer = null;
		}, NAV_EXPAND_ANIMATION_MS);
	};

	const openNavModal = (menuKey: string) => {
		if (!isMobileViewport()) return;
		clearCloseNavModalTimer();
		isModalClosing = false;
		activeModalKey = menuKey;
		isNavCompact = false;
	};

	const isExpandableMenuItem = (menuItemId?: string) => {
		return menuItemId === ASSISTANT_MENU_ITEM_ID;
	};

	const handleNavModalBackButton = (event: Event) => {
		event.preventDefault();
		closeNavModal();
	};

	const handleWindowKeydown = (event: KeyboardEvent) => {
		if (event.key !== 'Escape') return;
		if (!activeModalKey || isModalClosing) return;
		event.preventDefault();
		closeNavModal();
	};

	const getLongestHrefMatch = (items: MenuItemType[], loc: string) => {
		return items
			.filter((item) => item.href && loc.startsWith(item.href))
			.reduce<
				MenuItemType | undefined
			>((best, item) => ((item.href?.length ?? 0) > (best?.href?.length ?? 0) ? item : best), undefined);
	};

	const resolveLoadedMenuId = (
		loc: string,
		mainItems: MenuItemType[],
		secondaryItems: MenuItemType[],
	) => {
		if (getLongestHrefMatch(secondaryItems, loc)) return MORE_MENU_ITEM_ID;

		return (
			getLongestHrefMatch(
				mainItems.filter((item) => item.id !== ASSISTANT_MENU_ITEM_ID),
				loc,
			)?.id ?? null
		);
	};

	const handleAssistantOpenRequest = () => {
		if (activeModalKey === ASSISTANT_MENU_ITEM_ID) {
			closeNavModal();
			return;
		}
		openNavModal(ASSISTANT_MENU_ITEM_ID);
	};

	const handleClickNavItem = (menuItem: MenuItemType) => {
		if (isMobileViewport() && isExpandableMenuItem(menuItem.id)) {
			if (menuItem.id === ASSISTANT_MENU_ITEM_ID) {
				handleAssistantOpenRequest();
			}
			return;
		}
		handlerClickMenuItem(menuItem);
	};

	const handleCompactDismiss = () => {
		isNavCompact = false;
	};

	const HORIZONTAL_WHEEL_THRESHOLD_PX = 48;
	/** Ignore further wheel steps briefly so trackpad inertia cannot skip items. */
	const HORIZONTAL_WHEEL_STEP_COOLDOWN_MS = 320;
	const DRAG_INTENT_THRESHOLD_PX = 8;
	const DRAG_HORIZONTAL_RATIO = 1.25;
	let horizontalWheelAccum = 0;
	let horizontalWheelCooldownUntil = 0;
	let horizontalSelectBarEl: HTMLElement | null = null;
	let isDockIndicatorDragging = false;
	let dockDragPointerId: number | null = null;
	let dockDragStart: { x: number; y: number } | null = null;
	/** Selectable index at drag start — motion is clamped to ±1 neighbor. */
	let dockDragStartIndex = -1;
	let dockDragLeftIndex = -1;
	let dockDragRightIndex = -1;
	let dockDragMinX = 0;
	let dockDragMaxX = 0;
	/** Selectable index whose icon tint follows the scrub (midpoint handoff). */
	let dockDragPreviewIndex = -1;
	let dockDragArmed = false;
	let dockDragMoved = false;

	const getSelectableDockIndices = () => {
		return navItemsForRender
			.map((item, index) => ({ item, index }))
			.filter(({ item }) => item.id !== LOGO_MENU_ITEM_ID)
			.map(({ index }) => index);
	};

	const clampIndicatorX = (unclamped: number) => {
		if (!navBarRef || !navIndicatorRef) return 0;
		const maxIndicatorX =
			navBarRef.offsetWidth - NAV_EDGE_INSET * 2 - navIndicatorRef.offsetWidth;
		return Math.max(0, Math.min(unclamped, maxIndicatorX));
	};

	const getIndicatorXForIndex = (itemIndex: number) => {
		if (!navBarRef || !navIndicatorRef) return 0;
		const element = navItemsRefs[itemIndex];
		if (!element) return 0;
		const localCenter = element.offsetLeft + element.offsetWidth / 2;
		const unclamped = localCenter - navIndicatorRef.offsetWidth / 2 - NAV_EDGE_INSET;
		return clampIndicatorX(unclamped);
	};

	/** Bar-local indicatorX so the indicator center sits under clientX. */
	const indicatorXFromClientX = (clientX: number) => {
		if (!navBarRef || !navIndicatorRef) return indicatorX;
		const barRect = navBarRef.getBoundingClientRect();
		const localCenter = clientX - barRect.left;
		const unclamped =
			localCenter - navIndicatorRef.offsetWidth / 2 - NAV_EDGE_INSET;
		return clampIndicatorX(unclamped);
	};

	const armDockDragNeighbors = (startIndex: number) => {
		const indices = getSelectableDockIndices();
		let startPos = indices.indexOf(startIndex);
		if (startPos < 0) startPos = 0;
		const startIdx = indices[startPos] ?? startIndex;
		const leftIdx = startPos > 0 ? indices[startPos - 1] : startIdx;
		const rightIdx = startPos < indices.length - 1 ? indices[startPos + 1] : startIdx;
		const leftX = getIndicatorXForIndex(leftIdx);
		const rightX = getIndicatorXForIndex(rightIdx);
		dockDragStartIndex = startIdx;
		dockDragLeftIndex = leftIdx;
		dockDragRightIndex = rightIdx;
		dockDragMinX = Math.min(leftX, rightX);
		dockDragMaxX = Math.max(leftX, rightX);
		dockDragPreviewIndex = startIdx;
	};

	const updateDockDragPreviewFromIndicatorX = (x: number) => {
		const startX = getIndicatorXForIndex(dockDragStartIndex);
		const leftX = getIndicatorXForIndex(dockDragLeftIndex);
		const rightX = getIndicatorXForIndex(dockDragRightIndex);

		// Tint whichever selectable neighbor the plate is closest to (not only
		// after crossing the midpoint — keeps icon color locked to the selector).
		let next = dockDragStartIndex;
		let bestDist = Math.abs(x - startX);

		if (dockDragLeftIndex !== dockDragStartIndex) {
			const dist = Math.abs(x - leftX);
			if (dist < bestDist) {
				bestDist = dist;
				next = dockDragLeftIndex;
			}
		}
		if (dockDragRightIndex !== dockDragStartIndex) {
			const dist = Math.abs(x - rightX);
			if (dist < bestDist) {
				bestDist = dist;
				next = dockDragRightIndex;
			}
		}

		dockDragPreviewIndex = next;
	};

	/** Icon tint target — explicit so scrub preview invalidates the each-block. */
	$: dockHighlightIndex =
		isDockIndicatorDragging && dockDragPreviewIndex >= 0
			? dockDragPreviewIndex
			: activeRenderIndex;

	const activateDockItemAtIndex = (nextIndex: number) => {
		const menuItem = navItemsForRender[nextIndex];
		if (!menuItem || menuItem.id === LOGO_MENU_ITEM_ID) return;

		// Optimistic: keep icon tint on the scrub target while the route catches up.
		activeRenderIndex = nextIndex;

		if (menuItem.id === MORE_MENU_ITEM_ID) {
			// Leaving Assistant via scrub/wheel should collapse the sheet first.
			if (activeModalKey === ASSISTANT_MENU_ITEM_ID && !isModalClosing) {
				closeNavModal();
			}
			if (!showMoreDropdown) handleClickMoreBtn();
			return;
		}

		if (menuItem.id === ASSISTANT_MENU_ITEM_ID) {
			// Scrub release on Assistant must not toggle it closed.
			if (activeModalKey === ASSISTANT_MENU_ITEM_ID && !isModalClosing) return;
			handleCloseMoreBtn();
			handleClickNavItem(menuItem);
			return;
		}

		handleCloseMoreBtn();
		handleClickNavItem(menuItem);
	};

	const stepDockSelection = (direction: 1 | -1) => {
		if (!isNavDockHorizontalSelectEnabled()) return;
		if (isNavCompact) {
			isNavCompact = false;
			horizontalWheelAccum = 0;
			return;
		}

		const indices = getSelectableDockIndices();
		if (!indices.length) return;

		let currentPos = indices.indexOf(activeRenderIndex);
		if (currentPos < 0) {
			currentPos = direction > 0 ? -1 : indices.length;
		}

		const nextPos = currentPos + direction;
		if (nextPos < 0 || nextPos >= indices.length) return;

		activateDockItemAtIndex(indices[nextPos]);
	};

	const onDockHorizontalWheel = (event: WheelEvent) => {
		if (!isNavDockHorizontalSelectEnabled()) return;
		if (isDockIndicatorDragging) return;

		const absX = Math.abs(event.deltaX);
		const absY = Math.abs(event.deltaY);
		let along = 0;

		// Horizontal capsule: prefer deltaX (trackpad / mouse tilt wheel).
		if (absX >= absY || absX > 2) {
			along = event.deltaX;
		} else if (!isMobileViewport() && absY > absX) {
			// Desktop rotated rail: vertical wheel maps along the bar.
			along = event.deltaY;
		} else {
			return;
		}

		if (!along) return;
		event.preventDefault();

		const now = performance.now();
		if (now < horizontalWheelCooldownUntil) {
			horizontalWheelAccum = 0;
			return;
		}

		// Natural scrolling: finger/trackpad right → negative deltaX → selection right.
		horizontalWheelAccum += -along;
		if (Math.abs(horizontalWheelAccum) < HORIZONTAL_WHEEL_THRESHOLD_PX) return;

		const direction: 1 | -1 = horizontalWheelAccum > 0 ? 1 : -1;
		horizontalWheelAccum = 0;
		horizontalWheelCooldownUntil = now + HORIZONTAL_WHEEL_STEP_COOLDOWN_MS;
		stepDockSelection(direction);
	};

	const resetDockIndicatorDrag = () => {
		dockDragPointerId = null;
		dockDragStart = null;
		dockDragArmed = false;
		dockDragMoved = false;
		isDockIndicatorDragging = false;
		dockDragPreviewIndex = -1;
		dockDragStartIndex = -1;
		dockDragLeftIndex = -1;
		dockDragRightIndex = -1;
	};

	const endDockIndicatorDrag = (event: PointerEvent) => {
		if (dockDragPointerId === null) return;
		if (event.pointerId !== dockDragPointerId) return;

		const bar = horizontalSelectBarEl;
		const didDrag = dockDragArmed && dockDragMoved;
		const previewIndex = dockDragPreviewIndex;
		const pointerId = dockDragPointerId;

		if (bar) {
			try {
				bar.releasePointerCapture(pointerId);
			} catch {
				// Already released / lost
			}
		}

		resetDockIndicatorDrag();

		if (!didDrag) {
			void updateNavIndicator();
			return;
		}

		// Avoid a synthetic click on the item under the finger after a scrub.
		event.preventDefault();
		if (previewIndex >= 0) {
			activateDockItemAtIndex(previewIndex);
			// Keep plate + icon on the scrub target. Do not immediately re-sync from
			// the still-stale route (that snapped the tint back to the old item).
			// `updateNavIndicator` runs again when `loadedMenuId` / location updates.
			activeRenderIndex = previewIndex;
			indicatorVisible = true;
			indicatorX = getIndicatorXForIndex(previewIndex);
			return;
		}
		void updateNavIndicator();
	};

	const onDockPointerDown = (event: PointerEvent) => {
		if (!isNavDockHorizontalSelectEnabled()) return;
		if (event.button !== 0 && event.pointerType === 'mouse') return;
		// Allow scrub while Assistant is open so the user can drag back to a neighbor.

		if (isNavCompact) {
			isNavCompact = false;
			horizontalWheelAccum = 0;
			return;
		}

		dockDragPointerId = event.pointerId;
		dockDragStart = { x: event.clientX, y: event.clientY };
		dockDragStartIndex = activeRenderIndex;
		dockDragPreviewIndex = activeRenderIndex;
		dockDragArmed = false;
		dockDragMoved = false;
	};

	const onDockPointerMove = (event: PointerEvent) => {
		if (dockDragPointerId !== event.pointerId || !dockDragStart) return;
		if (!isNavDockHorizontalSelectEnabled()) return;

		const dx = event.clientX - dockDragStart.x;
		const dy = event.clientY - dockDragStart.y;

		if (!dockDragArmed) {
			const absX = Math.abs(dx);
			const absY = Math.abs(dy);
			if (absX < DRAG_INTENT_THRESHOLD_PX && absY < DRAG_INTENT_THRESHOLD_PX) return;
			// Vertical-dominant: leave the gesture to page scroll / item taps.
			if (absX < absY * DRAG_HORIZONTAL_RATIO) {
				resetDockIndicatorDrag();
				return;
			}
			dockDragArmed = true;
			isDockIndicatorDragging = true;
			dockDragMoved = true;
			armDockDragNeighbors(dockDragStartIndex >= 0 ? dockDragStartIndex : activeRenderIndex);
			horizontalSelectBarEl?.setPointerCapture(event.pointerId);
			event.preventDefault();
		}

		dockDragMoved = true;
		indicatorVisible = true;
		// Limit travel to the immediate neighbors — no overshoot past ±1 item.
		const nextX = Math.max(
			dockDragMinX,
			Math.min(dockDragMaxX, indicatorXFromClientX(event.clientX)),
		);
		indicatorX = nextX;
		updateDockDragPreviewFromIndicatorX(nextX);
		event.preventDefault();
	};

	const onDockPointerUp = (event: PointerEvent) => {
		endDockIndicatorDrag(event);
	};

	const onDockPointerCancel = (event: PointerEvent) => {
		endDockIndicatorDrag(event);
	};

	const onDockLostPointerCapture = (event: PointerEvent) => {
		// Browser / overlay stole the pointer — finish the gesture so scrubbing cannot stick.
		endDockIndicatorDrag(event);
	};

	const detachDockHorizontalSelect = () => {
		if (!horizontalSelectBarEl) return;
		horizontalSelectBarEl.removeEventListener('wheel', onDockHorizontalWheel);
		horizontalSelectBarEl.removeEventListener('pointerdown', onDockPointerDown);
		horizontalSelectBarEl.removeEventListener('pointermove', onDockPointerMove);
		horizontalSelectBarEl.removeEventListener('pointerup', onDockPointerUp);
		horizontalSelectBarEl.removeEventListener('pointercancel', onDockPointerCancel);
		horizontalSelectBarEl.removeEventListener('lostpointercapture', onDockLostPointerCapture);
		horizontalSelectBarEl = null;
		resetDockIndicatorDrag();
	};

	const attachDockHorizontalSelect = (bar: HTMLElement | undefined) => {
		if (bar === horizontalSelectBarEl) return;
		detachDockHorizontalSelect();
		if (!bar) return;
		horizontalSelectBarEl = bar;
		bar.addEventListener('wheel', onDockHorizontalWheel, { passive: false });
		bar.addEventListener('pointerdown', onDockPointerDown, { passive: true });
		bar.addEventListener('pointermove', onDockPointerMove, { passive: false });
		bar.addEventListener('pointerup', onDockPointerUp);
		bar.addEventListener('pointercancel', onDockPointerCancel);
		bar.addEventListener('lostpointercapture', onDockLostPointerCapture);
	};

	$: attachDockHorizontalSelect(navBarRef);

	const getItemKey = (menuItem: MenuItemType, index: number) => {
		return menuItem.id || menuItem.href || `${menuItem.label}-${index}`;
	};

	const updateNavIndicator = async () => {
		if (isDockIndicatorDragging) return;
		if (!navBarRef || !navIndicatorRef || !navShellRef || !navItemsForRender.length) return;
		await tick();
		if (isDockIndicatorDragging) return;

		const navBarWidth = navBarRef.offsetWidth;
		const indicatorWidth = navIndicatorRef.offsetWidth;
		const indicatorPositions = navItemsRefs.map((element) => {
			if (!element) return 0;
			const localCenter = element.offsetLeft + element.offsetWidth / 2;
			const unclampedIndicatorX = localCenter - indicatorWidth / 2 - NAV_EDGE_INSET;
			const maxIndicatorX = navBarWidth - NAV_EDGE_INSET * 2 - indicatorWidth;
			return Math.max(0, Math.min(unclampedIndicatorX, maxIndicatorX));
		});

		if (!indicatorPositions.length || !navBarWidth || !indicatorWidth) {
			indicatorX = 0;
			return;
		}

		const indicatorModalTarget = isModalClosing ? loadedMenuId : activeModalKey;
		const modalIndex = indicatorModalTarget
			? navItemsForRender.findIndex((item) => item.id === indicatorModalTarget)
			: -1;
		// Open "More" menu focuses the same way the assistant modal does.
		const moreFocusIndex = showMoreDropdown
			? navItemsForRender.findIndex((item) => item.id === MORE_MENU_ITEM_ID)
			: -1;
		const activeIndex =
			moreFocusIndex > -1
				? moreFocusIndex
				: modalIndex > -1
					? modalIndex
					: navItemsForRender.findIndex((item) => isMenuItemActive(item));
		const activeItem = activeIndex >= 0 ? navItemsForRender[activeIndex] : undefined;
		const hasSelectableDestination = !!activeItem && activeItem.id !== LOGO_MENU_ITEM_ID;

		activeRenderIndex = hasSelectableDestination ? activeIndex : -1;
		indicatorVisible = hasSelectableDestination;
		if (!hasSelectableDestination) return;

		indicatorX = indicatorPositions[activeIndex] ?? 0;
	};

	const handleCompactNavOnScroll = (scrollTop: number, scrollEl?: HTMLElement) => {
		// List/dashboard edit on mobile: keep the dock expanded (and inert) so DnD
		// chrome stays predictable — no scroll-driven compact animation.
		if ($edit && isMobileViewport()) {
			isNavCompact = false;
			return;
		}

		if (!isNavScrollCompactEnabled()) {
			isNavCompact = false;
			return;
		}

		// Near the end of the scroller: always restore the pill.
		// iOS overscroll often produces an upward delta here; Android usually does not.
		// Gated by Dev Tools → "Nav scroll bottom expand" (default on).
		const expandAtBottom =
			isNavScrollBottomExpandEnabled() && !!scrollEl && isScrollNearBottom(scrollEl);

		isNavCompact = resolveScrollChromeCollapsed(navScrollChrome, scrollTop, {
			isMobile: isMobileViewport(),
			forceExpanded: !!activeModalKey || expandAtBottom,
		});
	};

	const handleDocumentScroll = (event: Event) => {
		if (!isScrollChromeTarget(event.target)) return;
		handleCompactNavOnScroll(event.target.scrollTop, event.target);
	};

	const handleWindowScroll = () => {
		const scrollTop =
			window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
		const scrollEl =
			document.scrollingElement instanceof HTMLElement
				? document.scrollingElement
				: document.documentElement;
		handleCompactNavOnScroll(scrollTop, scrollEl);
	};

	const isMenuItemActive = (menuItem: MenuItemType) => {
		return menuItem.id === loadedMenuId;
	};

	$: menuOrder = appendMissingMenuOrderIndices(
		parseHomeDataMenuOrderJson($profile_data?.menuOrder),
		menuItemsNewLayout,
	);
	$: newLayoutOrder = partitionNewLayoutMenuOrderIndices(menuOrder);

	$: availableMenuItemIds = new Set(menuItems.map((menuItem) => menuItem.id));
	$: menuItemsPrimaryOrdered = newLayoutOrder.primary
		.map((position) => menuItemsNewLayout[position])
		.filter(
			(menuItem): menuItem is MenuItemType => !!menuItem && availableMenuItemIds.has(menuItem.id),
		);
	$: assistantMenuItem = menuItemsNewLayout[newLayoutOrder.assistant];
	$: menuItemsMainOrdered = assistantMenuItem
		? [...menuItemsPrimaryOrdered, assistantMenuItem]
		: menuItemsPrimaryOrdered;
	$: moreMenuItems = hasCameraDevices($devices)
		? [...menuItemsNewLayoutMore, menuItemCameraMonitoring]
		: menuItemsNewLayoutMore;
	$: loadedMenuId = resolveLoadedMenuId($location ?? '', menuItemsMainOrdered, moreMenuItems);
	$: isSettingsInMore = moreMenuItems.some((item) => item.href === '/settings');
	$: navItemsForRender = (() => {
		const items = [
			...(windowInnerWidth >= DESKTOP_NAV_BREAKPOINT ? [logoMenuItem] : []),
			...menuItemsMainOrdered,
		];

		const seen = new Set<string>();
		const result: MenuItemType[] = [];
		items.forEach((item, index) => {
			const key = getItemKey(item, index);
			if (seen.has(key)) return;
			seen.add(key);
			result.push(item);
		});

		result.push(moreMenuItem);
		return result;
	})();

	$: {
		navItemsForRender;
		activeMenuItem;
		visualActiveMenuId;
		activeModalKey;
		isModalClosing;
		showMoreDropdown;
		windowInnerWidth;
		$navIndicatorCircleEnabled;
		$navDockWideEnabled;
		if (!isDockIndicatorDragging) void updateNavIndicator();
	}

	$: if (activeModalKey && !isMobileViewport()) {
		clearCloseNavModalTimer();
		isModalClosing = false;
		activeModalKey = null;
	}

	$: if (activeModalKey) {
		isNavCompact = false;
	}

	// While the expanded surface is up, Android back collapses it instead of
	// running the app's default back navigation. The listener stays attached
	// through the collapse animation so a second press cannot navigate away.
	$: if (activeModalKey && mobile.isMobile) {
		if (!navModalBackButtonCleanup) {
			mobile.addEventListener('back_button', handleNavModalBackButton);
			navModalBackButtonCleanup = () => {
				mobile.removeEventListener('back_button', handleNavModalBackButton);
				navModalBackButtonCleanup = null;
			};
		}
	} else {
		navModalBackButtonCleanup?.();
		navModalBackButtonCleanup = null;
	}

	$: if ($edit && isMobileViewport() && isNavCompact) {
		isNavCompact = false;
	}

	onMount(() => {
		document.addEventListener('scroll', handleDocumentScroll, { capture: true, passive: true });
		window.addEventListener('scroll', handleWindowScroll, { passive: true });
	});

	onDestroy(() => {
		clearCloseNavModalTimer();
		navModalBackButtonCleanup?.();
		detachDockHorizontalSelect();
		document.removeEventListener('scroll', handleDocumentScroll, { capture: true });
		window.removeEventListener('scroll', handleWindowScroll);
	});
</script>

<svelte:window bind:innerWidth={windowInnerWidth} on:keydown={handleWindowKeydown} />

<PillShapedDock
	panelFlush
	highlightIconGrow
	indicatorCircle={$navIndicatorCircleEnabled}
	wide={$navDockWideEnabled}
	compact={isNavCompact}
	inert={$edit && isMobileViewport()}
	expanded={!!activeModalKey}
	expanding={!!activeModalKey && !isModalClosing}
	collapsing={isModalClosing}
	{indicatorX}
	{indicatorVisible}
	scrubbing={isDockIndicatorDragging}
	ariaLabel={$format('title_navigation', { default: 'Primary navigation' })}
	backdropCloseLabel={$format('button_close', { default: 'Close' })}
	bind:shellRef={navShellRef}
	bind:barRef={navBarRef}
	bind:indicatorRef={navIndicatorRef}
	on:backdrop-click={closeNavModal}
	on:compact-dismiss={handleCompactDismiss}
>
	<svelte:fragment slot="expanded">
		{#if activeModalKey === ASSISTANT_MENU_ITEM_ID}
			<button
				class="nav-assistant-close"
				type="button"
				aria-label={$format('button_close', { default: 'Close' })}
				on:click={closeNavModal}
			>
				×
			</button>
			<div class="nav-assistant-content">
				<ChatWindow
					surfaceOpen={activeModalKey === ASSISTANT_MENU_ITEM_ID && !isModalClosing}
				/>
			</div>
		{/if}
	</svelte:fragment>

	{#each navItemsForRender as menuItem, index (getItemKey(menuItem, index))}
		{@const isActive = index === dockHighlightIndex}
		{#if menuItem.id === LOGO_MENU_ITEM_ID}
			<button
				class="main-menu-item"
				type="button"
				aria-label={$format(menuItem.label, { default: menuItem.labelDefault })}
				style="--item-active-opacity:0;--item-active-scale:0;"
				bind:this={navItemsRefs[index]}
				on:click={handleLogoClick}
			>
				<img src="/images/shelly-logo-circle.svg" alt="" class="main-menu-item-logo" />
			</button>
		{:else if menuItem.id === MORE_MENU_ITEM_ID}
			{@const showMoreNotification = isSettingsInMore && $isEmailUnverified}
			<div
				class={`main-menu-item ${isActive ? 'is-active' : ''}`}
				style="--item-active-opacity:0;--item-active-scale:0;"
				bind:this={navItemsRefs[index]}
			>
				<DropdownMenu
					bind:show={showMoreDropdown}
					positionHorizontally={!isMobileViewport()}
					closeOnHoverOut={false}
					removeOverlay
					on:close={handleCloseMoreBtn}
				>
					<button
						slot="trigger"
						class="main-menu-more-trigger"
						type="button"
						aria-label={$format('title_more', { default: 'More' })}
						aria-expanded={showMoreDropdown}
						on:click={handleClickMoreBtn}
					>
						<i
							class={`main-menu-item-icon ${isActive ? getSolidIcon(MORE_MENU_ICON) : MORE_MENU_ICON}`}
							aria-hidden="true"
						></i>
						{#if showMoreNotification}
							<span class="main-menu-item-notification" aria-hidden="true"></span>
						{/if}
					</button>
					{#if showMoreDropdown}
						<MenuItemList isGroupTitle title={$format('title_more', { default: 'More' })} />
						{#each moreMenuItems as subMenuItem}
							<MenuItemList
								isGroupItem
								icon={subMenuItem.icon}
								title={$format(subMenuItem.label, {
									default: subMenuItem.labelDefault,
								})}
								active={isMoreSubItemActive(subMenuItem, $location ?? '')}
								on:click={() => handleClickMoreSubItem(subMenuItem)}
							/>
						{/each}
					{/if}
				</DropdownMenu>
			</div>
		{:else}
			{#if menuItem.id === ASSISTANT_MENU_ITEM_ID}
				<span class="main-menu-divider" aria-hidden="true"></span>
			{/if}
			<button
				class={`main-menu-item ${isActive ? 'is-active' : ''}`}
				type="button"
				aria-label={$format(menuItem.label, { default: menuItem.labelDefault })}
				style="--item-active-opacity:0;--item-active-scale:0;"
				bind:this={navItemsRefs[index]}
				on:click={() => {
					handleClickNavItem(menuItem);
				}}
			>
				<i class={`main-menu-item-icon ${getSolidIcon(menuItem.icon)}`} aria-hidden="true"></i>
			</button>
		{/if}
	{/each}
</PillShapedDock>

<style>
	/* The dock shell, item and indicator visuals live in the design system
	   (PillShapedDock). Only the assistant surface is styled here, since the
	   chat window is app-owned content rendered into the `expanded` slot. */
	.main-menu-more-trigger {
		display: grid;
		place-items: center;
		position: relative;
		width: 100%;
		height: 100%;
		padding: 0;
		border: 0;
		background: transparent;
		color: inherit;
		cursor: pointer;
	}

	.main-menu-item :global(.dropdown) {
		width: 100%;
		height: 100%;
	}

	.main-menu-item-notification {
		position: absolute;
		top: 6px;
		right: 10px;
		width: 8px;
		height: 8px;
		border-radius: 50%;
		background: var(--color-danger, #e53935);
		pointer-events: none;
	}

	/* Shared inset so the close control and message list stay aligned. */
	.nav-assistant-close,
	.nav-assistant-content {
		--nav-assistant-close-inset: var(--spacing-m, 16px);
		--nav-assistant-close-size: 2.5rem;
	}

	.nav-assistant-close {
		/* The expanded shell is already inset for the notch; do not add
		   safe-area again or the control sits too low in the chat panel. */
		position: absolute;
		top: var(--nav-assistant-close-inset);
		right: var(--nav-assistant-close-inset);
		width: var(--nav-assistant-close-size);
		height: var(--nav-assistant-close-size);
		border: 0;
		border-radius: 999px;
		background: rgb(0 0 0 / 34%);
		color: #fff;
		font-size: 1.5rem;
		cursor: pointer;
		z-index: 7;
		backdrop-filter: blur(4px);
		-webkit-backdrop-filter: blur(4px);
	}

	.nav-assistant-content {
		position: absolute;
		inset: 0;
	}

	.nav-assistant-content :global(.chat-window) {
		height: 100%;
	}

	.nav-assistant-content :global(.messages-container) {
		padding-top: calc(
			var(--nav-assistant-close-inset, var(--spacing-m, 16px)) +
				var(--nav-assistant-close-size, 2.5rem) + var(--spacing-s, 8px)
		);
	}

	.nav-assistant-content :global(.input-area) {
		padding-bottom: 0.75rem;
	}

	/* Phones: the expanded panel starts right below the safe area and spans the
	   full width. The dock otherwise adds `--nav-expand-step` (20px) below the
	   safe area and mirrors it as a side gap — space the chat can use instead. */
	@media (max-width: 639px) {
		:global(.main-menu-floating.is-expanded) {
			--nav-open-top: var(--ios-visual-viewport-offset-top, env(safe-area-inset-top, 0px));
			--nav-expanded-width: 100%;
		}
	}
</style>
